import React, { useCallback, useContext, useState } from 'react'

import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Comment, Position } from '@editor/interfaces/interfaces'
import {
  fetchComments,
  modifyComment,
  replaceComment,
  setEditingComment,
  setEditingCommentId,
  setIsCommentsSaving,
  setIsThreadsLoading,
  setSelectedComment,
} from '@editor/pages/projects/common/Comments/store/comments'
import { editComment } from '@editor/services/Comments'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

export const useComment = (comment: Comment) => {
  // Local states
  const [initialPosition, setInitialPosition] = useState<Position>({
    x: comment.cartesian_position?.x || 0,
    y: comment.cartesian_position?.y || 0,
    z: comment.cartesian_position?.z || 0,
  })

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)

  /**
   * On cancellation of moving comment.
   */
  const onCancelMove = useCallback(() => {
    dispatch(setEditingCommentId(null))
    dispatch(
      modifyComment({
        thread_id: comment.thread_id!,
        cartesian_position: comment.unplaced ? { x: 0, y: 0, z: 0 } : initialPosition,
      }),
    )
  }, [comment, dispatch, initialPosition])

  /**
   * Callback handler for handling saving of newly moved comment.
   */
  const onConfirmMove = useCallback(() => {
    if (!project || !inspectionArea) return

    dispatch(setIsCommentsSaving(true))
    void (async () => {
      if (!comment.cartesian_position) return

      const access_token = await getAccessToken()
      if (!access_token) {
        return
      }

      const result = await editComment(
        access_token,
        project.project_id,
        inspectionArea.inspection_area_id,
        comment,
        showErrorModal,
      )

      if (result) {
        dispatch(replaceComment({ thread_id: comment.thread_id!, comment: { ...result, images: comment.images } }))
        setInitialPosition(comment.cartesian_position)
        dispatch(setEditingCommentId(null))
        dispatch(setEditingComment(null))
      } else {
        onCancelMove()
      }
    })().finally(() => {
      dispatch(setIsCommentsSaving(false))
    })
  }, [comment, project, inspectionArea, dispatch, getAccessToken, showErrorModal, onCancelMove])
  /**
   * On click of comment icon.
   *
   * @param e Event
   * @returns
   */
  const onMouseUp = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.stopPropagation()

    // Parse the comment and see if its images has already expired
    const now = new Date()
    const expiredImages = comment.images?.filter((img) => {
      // It's an AWS signed URL so get the expiry from that
      if (!img.compressed_size_url) return false

      try {
        const params = new URL(img.compressed_size_url).searchParams
        const expiry = params.get('X-Amz-Expires')
        const issuedAt = params.get('X-Amz-Date')

        if (!expiry || !issuedAt || issuedAt.length < 15) return false

        const issuedAtDate = new Date(
          Date.UTC(
            parseInt(issuedAt.substring(0, 4), 10),
            parseInt(issuedAt.substring(4, 6), 10) - 1,
            parseInt(issuedAt.substring(6, 8), 10),
            parseInt(issuedAt.substring(9, 11), 10),
            parseInt(issuedAt.substring(11, 13), 10),
            parseInt(issuedAt.substring(13, 15), 10),
          ),
        )
        const expiryDate = new Date(issuedAtDate.getTime() + parseInt(expiry, 10) * 1000)
        return expiryDate < now
      } catch {
        return false
      }
    })

    // If there are already expired images, refetch the comments to get new signed URLs
    // Don't block the UI, just do it in the background so the comment will still open
    if (expiredImages && expiredImages.length > 0) {
      void (async () => {
        if (!project || !inspectionArea) return

        dispatch(setIsThreadsLoading(true))

        const access_token = await getAccessToken()
        if (!access_token) return

        await dispatch(
          fetchComments({
            access_token,
            project_id: project.project_id,
            inspection_area_id: inspectionArea.inspection_area_id,
            showErrorModal,
          }),
        )
      })().finally(() => {
        dispatch(setIsThreadsLoading(false))
      })
    }

    dispatch(setSelectedComment(comment))
    dispatch(setEditingCommentId(null))
  }

  return {
    onConfirmMove,
    onCancelMove,
    onMouseUp,
  }
}
